import * as THREE from 'three';
import { MMLCube } from './MMLParser';

// Interface para rastrear informações sobre parcelas animadas
interface AnimatedParcelInfo {
  id: string;
  currentY: number;
  previousY: number;
  isRising: boolean;
  isFalling: boolean;
  cube: MMLCube;
}

// Classe para rastrear parcelas animadas e suas posições
export class AnimatedParcelTracker {
  private parcels: Map<string, AnimatedParcelInfo> = new Map();
  private currentParcelId: string | null = null;

  // Registrar uma parcela animada
  registerParcel(cube: MMLCube, currentY: number): void {
    if (!cube.animations || cube.animations.length === 0) {
      return; // Não é uma parcela animada
    }

    // Verificar se a parcela já está registrada
    if (this.parcels.has(cube.id)) {
      const info = this.parcels.get(cube.id)!;
      info.previousY = info.currentY;
      info.currentY = currentY;

      // Determinar se está subindo ou descendo com uma pequena margem para evitar flutuações
      const diff = currentY - info.previousY;
      const threshold = 0.001; // Limiar para considerar movimento

      if (diff > threshold) {
        info.isRising = true;
        info.isFalling = false;
      } else if (diff < -threshold) {
        info.isRising = false;
        info.isFalling = true;
      } else {
        // Manter o estado anterior se a diferença for muito pequena
      }

      // Debug
      if (this.currentParcelId === cube.id) {
        console.log(`Parcela ${cube.id}: Y=${currentY}, Subindo=${info.isRising}, Descendo=${info.isFalling}`);
      }
    } else {
      // Registrar nova parcela
      this.parcels.set(cube.id, {
        id: cube.id,
        currentY,
        previousY: currentY,
        isRising: false,
        isFalling: false,
        cube
      });
    }
  }

  // Definir a parcela atual (onde a nave está)
  setCurrentParcel(parcelId: string | null): void {
    this.currentParcelId = parcelId;
  }

  // Verificar se a nave deve subir junto com a parcela
  shouldRiseWithParcel(): boolean {
    if (!this.currentParcelId) {
      return false;
    }

    const info = this.parcels.get(this.currentParcelId);
    if (!info) {
      return false;
    }

    return info.isRising;
  }

  // Obter a altura atual da parcela onde a nave está
  getCurrentParcelHeight(): number | null {
    if (!this.currentParcelId) {
      return null;
    }

    const info = this.parcels.get(this.currentParcelId);
    if (!info) {
      return null;
    }

    return info.currentY;
  }

  // Obter a diferença de altura desde a última atualização
  getHeightDifference(): number {
    if (!this.currentParcelId) {
      return 0;
    }

    const info = this.parcels.get(this.currentParcelId);
    if (!info) {
      return 0;
    }

    return info.currentY - info.previousY;
  }

  // Verificar se a parcela atual está subindo
  isCurrentParcelRising(): boolean {
    if (!this.currentParcelId) {
      return false;
    }

    const info = this.parcels.get(this.currentParcelId);
    if (!info) {
      return false;
    }

    return info.isRising;
  }

  // Verificar se a parcela atual está descendo
  isCurrentParcelFalling(): boolean {
    if (!this.currentParcelId) {
      return false;
    }

    const info = this.parcels.get(this.currentParcelId);
    if (!info) {
      return false;
    }

    return info.isFalling;
  }

  // Limpar todas as parcelas registradas
  clear(): void {
    this.parcels.clear();
    this.currentParcelId = null;
  }
}

// Exportar uma instância singleton do rastreador de parcelas animadas
export const animatedParcelTracker = new AnimatedParcelTracker();
